// ============================================
// M7.1 规则引擎配置页（云端基础规则）
// - 规则 CRUD：条件（json-rules-engine JSON）+ 动作（告警/Webhook/设备命令/广播）
// - 试跑（dry-run）：样例事件 → 命中规则与动作预览（不执行）
// - 执行记录：trace 审计（规则/设备/事件/动作结果/耗时）
// ============================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch,
  Table, Tabs, Tag, Typography, message, Alert, Tooltip, AutoComplete, Segmented,
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, ExperimentOutlined, DeleteOutlined, EditOutlined, ThunderboltOutlined, BookOutlined, SearchOutlined, RobotOutlined,
} from '@ant-design/icons';
import {
  createRule, deleteRule, fetchRuleExecutions, fetchRules, testRule, updateRule, aiDraftRule,
  RULE_ACTION_LABELS, type RuleAction, type RuleActionType, type RuleExecutionItem, type RuleItem, type RuleTestResult,
} from '../api/rules';
import RuleHelpModal from '../components/rule/RuleHelpModal';

const { Text, Paragraph } = Typography;

const EVENT_TEMPLATES: Record<string, string> = {
  'AI 检测（人数≥1）': JSON.stringify(
    { eventCode: 'AI_DETECTION', severity: 'info', module: 'ai_camera', imei: 'ATK1126B0000001', detail: { count: 2, detections: [{ class_name: 'person', confidence: 0.9 }] } },
    null, 2,
  ),
  'IO 触发': JSON.stringify(
    { eventCode: 'IO_TRIGGER', severity: 'info', module: 'ai_camera_io', imei: 'ATK1126B0000001', detail: { di: 'di1', value: 0 } },
    null, 2,
  ),
  '设备升级完成': JSON.stringify({ eventCode: 'UPGRADE_OK', imei: 'DTWIN-RK35880001', detail: {} }, null, 2),
};

const CONDITION_TEMPLATES: Record<string, string> = {
  '事件码等于': JSON.stringify({ all: [{ fact: 'eventCode', operator: 'equal', value: 'AI_DETECTION' }] }, null, 2),
  '检测人数≥阈值': JSON.stringify(
    { all: [{ fact: 'eventCode', operator: 'equal', value: 'AI_DETECTION' }, { fact: 'detail.count', operator: 'greaterThanInclusive', value: 3 }] },
    null, 2,
  ),
  'DI 触发': JSON.stringify(
    { all: [{ fact: 'eventCode', operator: 'equal', value: 'IO_TRIGGER' }, { fact: 'detail.di', operator: 'equal', value: 'di1' }] },
    null, 2,
  ),
  '类别包含 person': JSON.stringify(
    { all: [{ fact: 'detail.classes', operator: 'arrayContains', value: 'person' }] },
    null, 2,
  ),
};

// ── 2026-10-04：条件构建器（可视化）+ 规则向导（预置模板），JSON 保留高级模式 ──

interface CondRow {
  fact: string;
  operator: string;
  value: string;
}

const FACT_OPTIONS = ['eventCode', 'severity', 'module', 'imei', 'deviceId', 'detail.count', 'detail.di', 'detail.value', 'detail.classes'];
const OPERATOR_OPTIONS = [
  { value: 'equal', label: '等于 ==' },
  { value: 'notEqual', label: '不等于 !=' },
  { value: 'greaterThan', label: '大于 >' },
  { value: 'greaterThanInclusive', label: '大于等于 >=' },
  { value: 'lessThan', label: '小于 <' },
  { value: 'lessThanInclusive', label: '小于等于 <=' },
  { value: 'in', label: '在列表中 in' },
  { value: 'arrayContains', label: '数组包含' },
  { value: 'stringContains', label: '字符串包含' },
  { value: 'regexMatch', label: '正则匹配' },
];

function parseCondValue(operator: string, raw: string): unknown {
  const s = raw.trim();
  if (operator === 'in') {
    return s.split(',').map((x) => x.trim()).filter(Boolean).map((x) => (/^-?\d+(\.\d+)?$/.test(x) ? Number(x) : x));
  }
  if (s === 'true') return true;
  if (s === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(s)) return Number(s);
  return s;
}

function rowsToConditions(join: 'all' | 'any', rows: CondRow[]): Record<string, unknown> {
  const valid = rows.filter((r) => r.fact.trim() && r.operator && r.value.trim() !== '');
  return { [join]: valid.map((r) => ({ fact: r.fact.trim(), operator: r.operator, value: parseCondValue(r.operator, r.value) })) };
}

/** 简单结构 → 构建器行；不支持的复杂 JSON 返回 null（保持 JSON 高级模式） */
function conditionsToRows(c: unknown): { join: 'all' | 'any'; rows: CondRow[] } | null {
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
  const obj = c as Record<string, unknown>;
  const join = 'all' in obj ? 'all' : 'any' in obj ? 'any' : null;
  if (!join) return null;
  const arr = obj[join];
  if (!Array.isArray(arr)) return null;
  const rows: CondRow[] = [];
  for (const item of arr) {
    if (!item || typeof item !== 'object') return null;
    const it = item as Record<string, unknown>;
    if (!it.fact || !it.operator || !('value' in it)) return null;
    const v = it.value;
    if (v !== null && typeof v === 'object') return null; // 嵌套值走 JSON 模式
    rows.push({
      fact: String(it.fact),
      operator: String(it.operator),
      value: Array.isArray(v) ? v.join(',') : String(v),
    });
  }
  return { join, rows };
}

/** 规则向导：预置常见场景（名称 + 条件 + 动作），可继续微调 */
const RULE_WIZARDS: Array<{ key: string; label: string; name: string; join: 'all' | 'any'; rows: CondRow[]; actions: ActionDraft[] }> = [
  {
    key: 'offline', label: '设备离线告警', name: '设备离线告警', join: 'all',
    rows: [{ fact: 'eventCode', operator: 'equal', value: 'DEVICE_OFFLINE' }],
    actions: [{ type: 'alert', level: 'error', alertMessage: '设备离线，请检查网络/供电' }],
  },
  {
    key: 'threshold', label: '检测超阈值告警', name: 'AI 检测人数超阈值告警', join: 'all',
    rows: [
      { fact: 'eventCode', operator: 'equal', value: 'AI_DETECTION' },
      { fact: 'detail.count', operator: 'greaterThanInclusive', value: '3' },
    ],
    actions: [{ type: 'alert', level: 'warn', alertMessage: '检测人数超过阈值' }],
  },
  {
    key: 'linkage', label: 'IO 联动控制', name: 'IO 触发联动（闭合继电器）', join: 'all',
    rows: [
      { fact: 'eventCode', operator: 'equal', value: 'IO_TRIGGER' },
      { fact: 'detail.di', operator: 'equal', value: 'di1' },
    ],
    actions: [{ type: 'command', commandAction: 'write_do', commandParams: '{"channel":"rl1","value":1}' }],
  },
];

interface ActionDraft {
  type: RuleActionType;
  level?: string;
  alertMessage?: string;
  url?: string;
  timeoutMs?: number;
  commandAction?: string;
  commandParams?: string;
  event?: string;
}

function toDraft(a: RuleAction): ActionDraft {
  const p = a.params || {};
  return {
    type: a.type,
    level: (p.level as string) || 'warn',
    alertMessage: (p.message as string) || '',
    url: (p.url as string) || '',
    timeoutMs: Number(p.timeoutMs) || 5000,
    commandAction: (p.action as string) || '',
    commandParams: JSON.stringify(p.params || {}, null, 0),
    event: (p.event as string) || 'rule:triggered',
  };
}

function fromDraft(d: ActionDraft): RuleAction {
  switch (d.type) {
    case 'alert':
      return { type: 'alert', params: { level: d.level, message: d.alertMessage || undefined } };
    case 'webhook':
      return { type: 'webhook', params: { url: d.url, timeoutMs: d.timeoutMs } };
    case 'command': {
      let params: Record<string, unknown> = {};
      try { params = d.commandParams ? JSON.parse(d.commandParams) : {}; } catch { /* 交由服务端校验 */ }
      return { type: 'command', params: { action: d.commandAction, params } };
    }
    case 'broadcast':
      return { type: 'broadcast', params: { event: d.event } };
  }
}

function actionSummary(actions: RuleAction[]): string {
  return (actions || []).map((a) => {
    if (a.type === 'webhook') return `Webhook: ${String(a.params?.url || '')}`;
    if (a.type === 'command') return `命令: ${String(a.params?.action || '')}`;
    if (a.type === 'alert') return `告警: ${String(a.params?.level || 'warn')}`;
    return `广播: ${String(a.params?.event || 'rule:triggered')}`;
  }).join(' + ');
}

export default function RuleEngine() {
  const [rules, setRules] = useState<RuleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<RuleItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [actions, setActions] = useState<ActionDraft[]>([]);
  // 2026-10-04：条件构建器状态（可视化）与 JSON 高级模式
  const [condMode, setCondMode] = useState<'builder' | 'json'>('builder');
  const [condJoin, setCondJoin] = useState<'all' | 'any'>('all');
  const [condRows, setCondRows] = useState<CondRow[]>([{ fact: 'eventCode', operator: 'equal', value: '' }]);

  const [testOpen, setTestOpen] = useState(false);
  const [testEvent, setTestEvent] = useState(EVENT_TEMPLATES['AI 检测（人数≥1）']);
  const [testResult, setTestResult] = useState<RuleTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  // B3：AI 规则草稿（自然语言 → 规则 JSON；确认后保存）
  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);

  const [executions, setExecutions] = useState<RuleExecutionItem[]>([]);
  const [execLoading, setExecLoading] = useState(false);
  const [tab, setTab] = useState('rules');
  // 书本图标：新建/编辑规则详细说明（名词解释 + 用例）
  const [helpOpen, setHelpOpen] = useState(false);
  // 2026-10-10（用户要求）：规则列表搜索/排序/分页（参考可视化大屏列表）
  const [keyword, setKeyword] = useState('');
  const [sortKey, setSortKey] = useState<'priority' | 'updatedDesc' | 'name'>('priority');

  const filteredRules = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    let list = rules.filter(
      (r) =>
        !kw ||
        (r.name || '').toLowerCase().includes(kw) ||
        (r.description || '').toLowerCase().includes(kw) ||
        (r.eventCodes || []).join(',').toLowerCase().includes(kw),
    );
    switch (sortKey) {
      case 'name': list = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh')); break;
      case 'updatedDesc': list = [...list].sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()); break;
      default: list = [...list].sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100));
    }
    return list;
  }, [rules, keyword, sortKey]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await fetchRules());
    } catch (e) {
      message.error('规则加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExecutions = useCallback(async () => {
    setExecLoading(true);
    try {
      setExecutions(await fetchRuleExecutions({ limit: 100 }));
    } catch {
      message.error('执行记录加载失败');
    } finally {
      setExecLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'executions') loadExecutions(); }, [tab, loadExecutions]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true, priority: 100, cooldownMs: 0, conditions: CONDITION_TEMPLATES['事件码等于'] });
    setCondMode('builder');
    setCondJoin('all');
    setCondRows([{ fact: 'eventCode', operator: 'equal', value: '' }]);
    setActions([{ type: 'alert', level: 'warn', alertMessage: '' }]);
    setModalOpen(true);
  };

  /** B3：AI 草稿 → 填入新建表单（不保存） */
  const generateAiDraft = async () => {
    if (!aiPrompt.trim()) {
      message.warning('请描述规则需求，如：水温超过75度告警');
      return;
    }
    setAiLoading(true);
    try {
      const r = await aiDraftRule(aiPrompt.trim());
      setEditing(null);
      setAiWarnings(r.warnings ?? []);
      form.resetFields();
      form.setFieldsValue({
        name: r.draft.name,
        description: r.draft.description,
        enabled: r.draft.enabled,
        priority: r.draft.priority,
        cooldownMs: r.draft.cooldownMs,
        eventCodes: r.draft.eventCodes || [],
        conditions: JSON.stringify(r.draft.conditions, null, 2),
      });
      const conv = conditionsToRows(r.draft.conditions);
      if (conv) {
        setCondJoin(conv.join);
        setCondRows(conv.rows);
        setCondMode('builder');
      } else {
        setCondRows([]);
        setCondMode('json');
      }
      setActions((r.draft.actions || []).map(toDraft));
      setAiOpen(false);
      setModalOpen(true);
      message.success('已生成规则草稿，请核对后保存');
    } catch (e) {
      message.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'AI 草稿生成失败');
    } finally {
      setAiLoading(false);
    }
  };

  const openEdit = (r: RuleItem) => {
    setEditing(r);
    form.setFieldsValue({
      name: r.name, description: r.description, enabled: r.enabled, priority: r.priority,
      cooldownMs: r.cooldownMs, eventCodes: r.eventCodes || [],
      conditions: JSON.stringify(r.conditions, null, 2),
    });
    const conv = conditionsToRows(r.conditions);
    if (conv) {
      setCondJoin(conv.join);
      setCondRows(conv.rows);
      setCondMode('builder');
    } else {
      setCondRows([]);
      setCondMode('json');
    }
    setActions((r.actions || []).map(toDraft));
    setModalOpen(true);
  };

  /** 可视化 ⇄ JSON 切换（JSON→可视化仅支持简单结构，复杂结构保持 JSON） */
  const switchCondMode = (next: 'builder' | 'json') => {
    if (next === condMode) return;
    if (next === 'json') {
      form.setFieldValue('conditions', JSON.stringify(rowsToConditions(condJoin, condRows), null, 2));
    } else {
      let parsed: unknown = null;
      try { parsed = JSON.parse(String(form.getFieldValue('conditions') ?? '')); } catch { /* ignore */ }
      const conv = parsed ? conditionsToRows(parsed) : null;
      if (!conv) {
        message.warning('该条件结构较复杂（如嵌套 any/all），请继续使用 JSON 高级模式');
        return;
      }
      setCondJoin(conv.join);
      setCondRows(conv.rows);
    }
    setCondMode(next);
  };

  const updCondRow = (i: number, patch: Partial<CondRow>) => {
    setCondRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const applyWizard = (key: string) => {
    const w = RULE_WIZARDS.find((x) => x.key === key);
    if (!w) return;
    form.setFieldsValue({ name: w.name });
    setCondMode('builder');
    setCondJoin(w.join);
    setCondRows(w.rows.map((r) => ({ ...r })));
    setActions(w.actions.map((a) => ({ ...a })));
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      let conditions: Record<string, unknown>;
      if (condMode === 'builder') {
        const validCount = condRows.filter((r) => r.fact.trim() && r.operator && r.value.trim() !== '').length;
        if (validCount === 0) {
          message.error('请至少添加一个有效条件（字段/算子/值）');
          return;
        }
        conditions = rowsToConditions(condJoin, condRows);
      } else {
        try {
          conditions = JSON.parse(values.conditions);
        } catch {
          message.error('条件 JSON 解析失败');
          return;
        }
      }
      if (actions.length === 0) { message.error('至少一个动作'); return; }
      const payload = {
        name: values.name,
        description: values.description,
        enabled: values.enabled,
        priority: values.priority ?? 100,
        cooldownMs: values.cooldownMs ?? 0,
        eventCodes: values.eventCodes?.length ? values.eventCodes : null,
        conditions,
        actions: actions.map(fromDraft),
      };
      setSaving(true);
      if (editing) {
        await updateRule(editing.id, payload);
        message.success('规则已更新');
      } else {
        await createRule(payload);
        message.success('规则已创建');
      }
      setModalOpen(false);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      if (err?.response?.data?.message) message.error(String(err.response.data.message));
      else if (err?.message && !String(err.message).includes('validate')) message.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (r: RuleItem, enabled: boolean) => {
    try {
      await updateRule(r.id, { enabled });
      setRules((prev) => prev.map((x) => (x.id === r.id ? { ...x, enabled } : x)));
    } catch {
      message.error('切换失败（可能无权操作全局规则）');
    }
  };

  const runTest = async () => {
    try {
      const event = JSON.parse(testEvent);
      setTesting(true);
      setTestResult(await testRule(event));
    } catch (e) {
      message.error(e instanceof SyntaxError ? '事件 JSON 解析失败' : '试跑失败');
    } finally {
      setTesting(false);
    }
  };

  const columns = [
    {
      title: '名称', dataIndex: 'name', key: 'name',
      render: (v: string, r: RuleItem) => (
        <Space orientation="vertical" size={0}>
          <Space size={6}>
            <Text strong>{v}</Text>
            {!r.organizationId && <Tag color="purple">全局</Tag>}
          </Space>
          {r.description && <Text type="secondary" style={{ fontSize: 12 }}>{r.description}</Text>}
        </Space>
      ),
    },
    { title: '优先级', dataIndex: 'priority', key: 'priority', width: 80 },
    {
      title: '事件码', dataIndex: 'eventCodes', key: 'eventCodes', width: 180,
      render: (v: string[] | null) => (v?.length ? v.map((c) => <Tag key={c}>{c}</Tag>) : <Text type="secondary">全部事件</Text>),
    },
    {
      title: '动作', key: 'actions', width: 240,
      render: (_: unknown, r: RuleItem) => <Text style={{ fontSize: 12 }}>{actionSummary(r.actions)}</Text>,
    },
    {
      title: '启用', key: 'enabled', width: 70,
      render: (_: unknown, r: RuleItem) => <Switch size="small" checked={r.enabled} onChange={(v) => toggle(r, v)} />,
    },
    {
      title: '操作', key: 'ops', width: 130,
      render: (_: unknown, r: RuleItem) => (
        <Space size={4}>
          <Tooltip title="编辑"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
          <Popconfirm title="删除该规则？" onConfirm={async () => { await deleteRule(r.id); message.success('已删除'); load(); }}>
            <Button size="small" type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const execColumns = [
    { title: '时间', dataIndex: 'createdAt', key: 'createdAt', width: 170, render: (v: string) => new Date(v).toLocaleString() },
    { title: '规则', dataIndex: 'ruleName', key: 'ruleName', width: 180 },
    { title: '设备', dataIndex: 'imei', key: 'imei', width: 180 },
    { title: '事件', dataIndex: 'eventCode', key: 'eventCode', width: 140, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: '动作结果', key: 'actions',
      render: (_: unknown, r: RuleExecutionItem) => (
        <Space size={4} wrap>
          {(r.actions || []).map((a, i) => (
            <Tag key={i} color={a.ok ? 'green' : 'red'}>{a.type} {a.ok ? `${a.ms}ms` : a.error}</Tag>
          ))}
        </Space>
      ),
    },
    { title: '耗时', dataIndex: 'durationMs', key: 'durationMs', width: 80, render: (v: number) => `${v}ms` },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={<Space><ThunderboltOutlined />规则引擎（M7）</Space>}
        extra={
          <Space wrap>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="按名称/说明/事件码搜索"
              style={{ width: 200 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              data-testid="rule-search"
            />
            <Select
              value={sortKey}
              onChange={(v) => setSortKey(v)}
              style={{ width: 150 }}
              data-testid="rule-sort"
              options={[
                { value: 'priority', label: '优先级（小=先）' },
                { value: 'updatedDesc', label: '更新时间↓ 新→旧' },
                { value: 'name', label: '名称排序' },
              ]}
            />
            <Button icon={<ExperimentOutlined />} onClick={() => { setTestResult(null); setTestOpen(true); }}>试跑</Button>
            <Button icon={<RobotOutlined />} onClick={() => { setAiPrompt(''); setAiWarnings([]); setAiOpen(true); }} data-testid="rule-ai-btn">AI 生成</Button>
            <Button icon={<ReloadOutlined />} onClick={() => (tab === 'rules' ? load() : loadExecutions())}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>新建规则</Button>
          </Space>
        }
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'rules',
              label: `规则列表（${filteredRules.length}）`,
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  loading={loading}
                  columns={columns}
                  dataSource={filteredRules}
                  pagination={{ pageSize: 10, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
                />
              ),
            },
            {
              key: 'executions',
              label: '执行记录',
              children: (
                <Table rowKey="id" size="small" loading={execLoading} columns={execColumns} dataSource={executions} pagination={{ pageSize: 20 }} />
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 26 }}>
            <span>{editing ? `编辑规则：${editing.name}` : '新建规则'}</span>
            <Tooltip title="规则使用说明：名词解释 + 用例（设备离线/超阈值/IO 联动）">
              <Button size="small" type="text" icon={<BookOutlined />} onClick={() => setHelpOpen(true)} data-testid="rule-help-btn">
                说明
              </Button>
            </Tooltip>
          </div>
        )}
        open={modalOpen}
        onOk={save}
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        width={760}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="name" label="名称" tooltip="规则名称（业务可读，出现在告警与执行记录中）。使用案例：AI 检测人数超阈值告警 / 车间烟感离线告警" rules={[{ required: true, message: '请输入名称' }]} style={{ flex: 1 }}>
              <Input placeholder="如：AI 检测人数超限告警" maxLength={100} />
            </Form.Item>
            <Form.Item name="priority" label="优先级（小=先）" tooltip="数字越小越先评估；多条规则可同时命中并各自执行。使用案例：重要告警填 10，常规填 100" style={{ width: 140 }}>
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="enabled" label="启用" tooltip="关闭后配置保留但不参与评估；建议先在「试跑」验证命中，再开启并保存" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
          <Form.Item name="description" label="说明" tooltip="可选备注：维护人 / 适用产线 / 变更原因。使用案例：夜班产线 A 专用，联系张三">
            <Input placeholder="可选" maxLength={200} />
          </Form.Item>
          <Space size={12} style={{ display: 'flex' }}>
            <Form.Item name="eventCodes" label="事件码快筛（空=全部事件）" tooltip="只把列出的设备事件送入本规则评估（性能过滤，不替代「条件」判断）。使用案例：只关心 AI_DETECTION 时填它；IO 联动填 IO_TRIGGER" style={{ flex: 1 }}>
              <Select mode="tags" placeholder="如 AI_DETECTION / IO_TRIGGER / UPGRADE_OK" open={false} suffixIcon={null} />
            </Form.Item>
            <Form.Item name="cooldownMs" label="冷却（ms）" tooltip="同规则在该时间窗口内最多触发一次，防抖动刷屏。使用案例：60000 = 1 分钟最多一次；0 = 不限制" style={{ width: 140 }}>
              <InputNumber min={0} max={3600000} step={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Space>

          <Form.Item label="条件" required data-testid="rule-condition-block" tooltip="命中判断（支持 detail.xxx 点路径）。使用案例：AI_DETECTION 且 detail.count>=3；可点「规则向导」一键生成，或切「JSON 高级」手写；建议配合「试跑」验证">
            <Space size={6} wrap style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>规则向导：</Text>
              {RULE_WIZARDS.map((w) => (
                <Tag
                  key={w.key}
                  color="blue"
                  style={{ cursor: 'pointer' }}
                  data-testid={`rule-wizard-${w.key}`}
                  onClick={() => applyWizard(w.key)}
                >
                  {w.label}
                </Tag>
              ))}
              <Segmented
                size="small"
                value={condMode}
                onChange={(v) => switchCondMode(v as 'builder' | 'json')}
                options={[{ label: '可视化', value: 'builder' }, { label: 'JSON 高级', value: 'json' }]}
                data-testid="rule-cond-mode"
              />
            </Space>

            {condMode === 'builder' ? (
              <div style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 10 }}>
                <Space size={8} style={{ marginBottom: 8 }}>
                  <Select
                    size="small"
                    style={{ width: 150 }}
                    value={condJoin}
                    onChange={setCondJoin}
                    options={[
                      { value: 'all', label: '全部满足（AND）' },
                      { value: 'any', label: '任一满足（OR）' },
                    ]}
                  />
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    data-testid="rule-cond-add"
                    onClick={() => setCondRows([...condRows, { fact: 'eventCode', operator: 'equal', value: '' }])}
                  >
                    添加条件
                  </Button>
                </Space>
                {condRows.map((r, i) => (
                  <Space key={i} size={6} style={{ display: 'flex', marginBottom: 6 }}>
                    <AutoComplete
                      size="small"
                      style={{ width: 190 }}
                      value={r.fact}
                      options={FACT_OPTIONS.map((v) => ({ value: v }))}
                      placeholder="字段（支持 detail.xxx）"
                      data-testid={`rule-cond-fact-${i}`}
                      filterOption={(input, option) => String(option?.value ?? '').toLowerCase().includes(String(input).toLowerCase())}
                      onChange={(v) => updCondRow(i, { fact: v })}
                    />
                    <Select
                      size="small"
                      style={{ width: 160 }}
                      value={r.operator}
                      options={OPERATOR_OPTIONS}
                      onChange={(v) => updCondRow(i, { operator: v })}
                    />
                    <Input
                      size="small"
                      style={{ width: 220 }}
                      value={r.value}
                      placeholder={r.operator === 'in' ? '值（逗号分隔）' : '值'}
                      data-testid={`rule-cond-value-${i}`}
                      onChange={(e) => updCondRow(i, { value: e.target.value })}
                    />
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setCondRows(condRows.filter((_, x) => x !== i))}
                    />
                  </Space>
                ))}
                <Paragraph type="secondary" style={{ fontSize: 11, margin: 0 }}>
                  字段支持点路径（detail.xxx）；算子：equal/notEqual/greaterThan(Inclusive)/lessThan(Inclusive)/in/arrayContains/stringContains/regexMatch。切换「JSON 高级」可查看/微调生成的 JSON。
                </Paragraph>
              </div>
            ) : (
              <>
                <Space size={6} wrap style={{ marginBottom: 6 }}>
                  {Object.keys(CONDITION_TEMPLATES).map((k) => (
                    <Tag key={k} style={{ cursor: 'pointer' }} onClick={() => form.setFieldsValue({ conditions: CONDITION_TEMPLATES[k] })}>{k}</Tag>
                  ))}
                  <Text type="secondary" style={{ fontSize: 12 }}>算子：equal/notEqual/greaterThan(Inclusive)/lessThan(Inclusive)/in/arrayContains/stringContains/regexMatch</Text>
                </Space>
                <Form.Item name="conditions" noStyle rules={[{ required: true, message: '请输入条件 JSON' }]}>
                  <Input.TextArea rows={5} style={{ fontFamily: 'monospace', fontSize: 12 }} />
                </Form.Item>
              </>
            )}
          </Form.Item>

          <Form.Item label="动作" required tooltip="命中后执行的操作，可组合多个。使用案例：告警（level=warn + 文案）；命令（write_do 闭合继电器）；Webhook（通知三方系统）；广播（大屏自定义事件）">
            {actions.map((a, idx) => (
              <Card key={idx} size="small" style={{ marginBottom: 8 }}
                extra={<Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => setActions(actions.filter((_, i) => i !== idx))} />}>
                <Space orientation="vertical" style={{ width: '100%' }} size={6}>
                  <Space size={8} wrap>
                    <Select
                      style={{ width: 220 }} value={a.type}
                      options={Object.entries(RULE_ACTION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
                      onChange={(v: RuleActionType) => setActions(actions.map((x, i) => (i === idx ? { ...x, type: v } : x)))}
                    />
                    {a.type === 'alert' && (
                      <>
                        <Select style={{ width: 100 }} value={a.level} options={['info', 'warn', 'error'].map((v) => ({ value: v, label: v }))}
                          onChange={(v) => setActions(actions.map((x, i) => (i === idx ? { ...x, level: v } : x)))} />
                        <Input style={{ width: 300 }} placeholder="告警文案" value={a.alertMessage}
                          onChange={(e) => setActions(actions.map((x, i) => (i === idx ? { ...x, alertMessage: e.target.value } : x)))} />
                      </>
                    )}
                    {a.type === 'webhook' && (
                      <>
                        <Input style={{ width: 320 }} placeholder="https://第三方平台/回调" value={a.url}
                          onChange={(e) => setActions(actions.map((x, i) => (i === idx ? { ...x, url: e.target.value } : x)))} />
                        <InputNumber style={{ width: 110 }} addonAfter="ms" value={a.timeoutMs}
                          onChange={(v) => setActions(actions.map((x, i) => (i === idx ? { ...x, timeoutMs: Number(v) || 5000 } : x)))} />
                      </>
                    )}
                    {a.type === 'command' && (
                      <>
                        <Input style={{ width: 180 }} placeholder="契约命令名（如 reboot）" value={a.commandAction}
                          onChange={(e) => setActions(actions.map((x, i) => (i === idx ? { ...x, commandAction: e.target.value } : x)))} />
                        <Input style={{ width: 260 }} placeholder='命令参数 JSON（如 {"channel":"rl1","value":1}）' value={a.commandParams}
                          onChange={(e) => setActions(actions.map((x, i) => (i === idx ? { ...x, commandParams: e.target.value } : x)))} />
                      </>
                    )}
                    {a.type === 'broadcast' && (
                      <Input style={{ width: 260 }} placeholder="WS 事件名（默认 rule:triggered）" value={a.event}
                        onChange={(e) => setActions(actions.map((x, i) => (i === idx ? { ...x, event: e.target.value } : x)))} />
                    )}
                  </Space>
                </Space>
              </Card>
            ))}
            <Button size="small" icon={<PlusOutlined />} onClick={() => setActions([...actions, { type: 'alert', level: 'warn' }])}>添加动作</Button>
          </Form.Item>
        </Form>
      </Modal>

      <RuleHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />

      <Modal
        title={<Space><ExperimentOutlined />规则试跑（dry-run，不执行动作）</Space>}
        open={testOpen}
        onCancel={() => setTestOpen(false)}
        footer={<Button type="primary" loading={testing} onClick={runTest}>运行</Button>}
        width={680}
      >
        <Space size={6} wrap style={{ marginBottom: 8 }}>
          {Object.keys(EVENT_TEMPLATES).map((k) => (
            <Tag key={k} style={{ cursor: 'pointer' }} onClick={() => setTestEvent(EVENT_TEMPLATES[k])}>{k}</Tag>
          ))}
        </Space>
        <Input.TextArea rows={8} style={{ fontFamily: 'monospace', fontSize: 12 }} value={testEvent} onChange={(e) => setTestEvent(e.target.value)} />
        {testResult && (
          <div style={{ marginTop: 12 }}>
            <Alert
              type={testResult.triggered.length > 0 ? 'success' : 'info'}
              message={`评估 ${testResult.evaluated} 条规则，命中 ${testResult.triggered.length} 条${testResult.errors.length ? `；异常 ${testResult.errors.join('；')}` : ''}`}
            />
            {testResult.triggered.map((t) => (
              <Card key={t.ruleId} size="small" style={{ marginTop: 8 }} title={t.ruleName}>
                <Space size={6} wrap>
                  {t.actions.map((a, i) => <Tag key={i} color="blue">{a.type}（dry-run）</Tag>)}
                </Space>
              </Card>
            ))}
          </div>
        )}
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
          事件字段：eventCode/severity/module/imei/deviceId/detail.*；试跑按当前登录租户的规则 + 全局规则评估。
        </Paragraph>
      </Modal>

      {/* B3：AI 规则草稿 */}
      <Modal
        title={<Space><RobotOutlined /> AI 生成规则草稿</Space>}
        open={aiOpen}
        onOk={() => void generateAiDraft()}
        okText="生成草稿"
        confirmLoading={aiLoading}
        onCancel={() => setAiOpen(false)}
        destroyOnHidden
        width={560}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size={10}>
          <Input.TextArea
            rows={3}
            placeholder="用一句话描述规则，如：水温超过75度告警并写日志；或：设备离线超过10分钟通知运维"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            data-testid="rule-ai-prompt"
          />
          {aiWarnings.length > 0 ? (
            <Alert type="warning" showIcon message={`提示：${aiWarnings.join('；')}`} />
          ) : null}
          <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
            生成的是草稿（默认未启用），将自动填入规则表单；请核对条件/动作与事件码后再保存。
          </Paragraph>
        </Space>
      </Modal>
    </div>
  );
}
